import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Zap, Check, ArrowRight, QrCode, BellRing, FileSpreadsheet, ShieldCheck,
  Wallet, DatabaseBackup, Loader2, Globe, Gauge, Truck, Receipt, X, Infinity, Lock, CreditCard, Gift,
} from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import EmbeddedCheckoutModal from "../components/EmbeddedCheckoutModal";
import DemoVideo from "../components/DemoVideo";

const CONTROL_IMG = "https://images.unsplash.com/photo-1772440223098-cc23f6f01209?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const T = {
  it: {
    nav_features: "Funzioni",
    nav_pricing: "Prezzi",
    login: "Accedi",
    hero_badge: "Gestione flotte · PWA",
    hero_title_1: "La tua flotta,",
    hero_title_2: "sotto controllo.",
    claim: "Facile, veloce e sempre con te!",
    hero_sub: "Thoth digitalizza mezzi, segnalazioni, scadenze, costi e fatture. Gli operatori segnalano un guasto inquadrando un QR, tu vedi tutto in tempo reale.",
    cta_start: "Prova gratis 3 giorni",
    cta_demo: "Entra nel pannello",
    stat_vehicles: "mezzi gestiti",
    stat_qr: "nessuna app da scaricare",
    stat_realtime: "dati in tempo reale",
    features_kicker: "Tutto in un'unica app",
    features_title: "Dalla segnalazione alla fattura, senza carta",
    f_qr_t: "Segnalazioni via QR",
    f_qr_d: "Ogni mezzo ha un QR: l'operatore inquadra, descrive il guasto e allega una foto. Nessun login, nessuna app.",
    f_deadlines_t: "Scadenze e promemoria",
    f_deadlines_d: "Revisione, assicurazione, tagliando e chilometraggio: avvisi automatici via push ed email prima che scadano.",
    f_costs_t: "Costi e fatture",
    f_costs_d: "Traccia ogni spesa per mezzo e officina, carica le fatture e tieni sotto controllo il budget della flotta.",
    f_import_t: "Importa i tuoi dati",
    f_import_d: "Porti i tuoi archivi da Excel/CSV in pochi click: mezzi, interventi, costi e operatori.",
    f_backup_t: "Backup automatici",
    f_backup_d: "Copia di sicurezza giornaliera dell'intero database, scaricabile e ripristinabile quando vuoi.",
    f_roles_t: "Ruoli e permessi",
    f_roles_d: "Amministratori, magazzinieri e operatori: ognuno vede ciò che gli serve. Log attività completo.",
    control_kicker: "Pannello di controllo",
    control_title: "Un colpo d'occhio su tutta la flotta",
    control_d: "Dashboard con stato dei mezzi, segnalazioni aperte, scadenze imminenti e costi. Esporta tutto in CSV o PDF in un attimo.",
    control_b1: "Stato mezzi in tempo reale",
    control_b2: "Storico interventi per mezzo e per officina",
    control_b3: "Report mensili e export professionali",
    pricing_kicker: "Prezzi semplici",
    pricing_title: "Un unico piano, tutto incluso",
    pricing_sub: "Thoth Pro: tutte le funzioni, mezzi illimitati. Inizi con 3 giorni di prova gratuita, poi disdici quando vuoi.",
    monthly: "Mensile",
    yearly: "Annuale",
    save_badge: "Risparmia 2 mesi",
    per_month: "/mese",
    per_year: "/anno",
    billed_yearly: "fatturato annualmente",
    most_popular: "Più scelto",
    choose: "Scegli",
    processing: "Attendere…",
    faq_title: "Domande frequenti",
    faq: [
      { q: "C'è una prova gratuita?", a: "Sì: 3 giorni con tutte le funzioni Pro, senza inserire la carta. Alla scadenza attivi l'abbonamento dall'area Abbonamento per continuare, i tuoi dati restano al sicuro." },
      { q: "Serve installare qualcosa?", a: "No. Thoth è una web app installabile come PWA sul telefono. Gli operatori usano solo il QR, senza scaricare nulla." },
      { q: "Posso portare i miei dati?", a: "Sì, puoi importare mezzi, interventi, costi e operatori da file Excel o CSV in autonomia." },
      { q: "I pagamenti sono sicuri?", a: "Sì, il checkout è gestito da Stripe. Non memorizziamo i dati della tua carta." },
      { q: "Posso cambiare piano?", a: "Thoth ha un unico piano Pro con tutte le funzioni incluse. Puoi disdire quando vuoi direttamente dall'area Abbonamento." },
    ],
    footer_tag: "Facile, veloce e sempre con te!",
    footer_rights: "Tutti i diritti riservati.",
    op_portal: "Portale operatori",
    secure: "Pagamenti sicuri con Stripe",
    hero_free: "3 giorni di prova gratuita · nessuna carta richiesta",
    hero_video_cta: "Guarda la demo (45 s)",
    hero_video_close: "Chiudi",
    free_price: "Gratis",
    free_cta: "Inizia gratis",
    subscribe_cta: "Abbonati",
    signup_title: "Crea il tuo account",
    signup_sub: "Hai 3 giorni di prova gratuita con tutte le funzioni Pro, senza carta di credito. Alla scadenza scegli se attivare l'abbonamento.",
    upgrade_note: "Dopo la registrazione potrai completare subito il pagamento sicuro via Stripe, oppure chiudere e iniziare dalla prova gratuita di 3 giorni.",
    f_company: "Nome azienda",
    f_firstname: "Nome",
    f_lastname: "Cognome",
    f_email: "Email",
    f_password: "Password (min 8 caratteri)",
    signup_btn: "Crea account",
    signup_processing: "Creazione account…",
    terms_pre: "Accetto i ",
    terms_link: "Termini e Condizioni",
    terms_post: " e l'informativa sulla privacy.",
    terms_required: "Devi accettare i Termini e Condizioni",
    signup_success: "Benvenuto in Thoth! La tua prova gratuita di 3 giorni è iniziata.",
    have_account: "Hai già un account?",
    login_link: "Accedi",
    selected_plan: "Piano selezionato",
    lifetime_title: "Acquisto a vita",
    lifetime_sub: "Paga una volta e usa Thoth per sempre: tutte le funzioni Pro, nessun rinnovo, nessun canone mensile.",
    lifetime_cta: "Acquista a vita",
    one_time: "una tantum",
    lifetime_payoff: "Si ripaga in ~{y} anni rispetto all'abbonamento annuale Pro",
    save_yearly: "Risparmi",
    vs_monthly: "all'anno vs mensile",
    equiv: "equivale a",
    sec_kicker: "Sicurezza & Fiducia",
    sec_title: "I tuoi dati protetti, sempre",
    sec_d: "Thoth è costruito con la sicurezza al primo posto: dati isolati per azienda, backup giornalieri e pagamenti gestiti da Stripe.",
    sec_1_t: "Dati cifrati e isolati",
    sec_1_d: "Ogni azienda ha i propri dati completamente separati. Connessioni cifrate (HTTPS) e password protette con hashing bcrypt.",
    sec_2_t: "Backup automatici giornalieri",
    sec_2_d: "Copia di sicurezza dell'intero database ogni notte, ripristinabile in qualsiasi momento. Non perdi mai nulla.",
    sec_3_t: "Conforme al GDPR",
    sec_3_d: "Rispettiamo la normativa europea sulla privacy. Puoi esportare o richiedere la cancellazione dei tuoi dati quando vuoi.",
    sec_4_t: "Pagamenti sicuri con Stripe",
    sec_4_d: "Il checkout è gestito da Stripe, leader mondiale dei pagamenti. Non memorizziamo i dati della tua carta.",
    sec_payments_soon: "Presto saranno disponibili altri metodi di pagamento (PayPal, bonifico e altri).",
  },
  en: {
    nav_features: "Features",
    nav_pricing: "Pricing",
    login: "Log in",
    hero_badge: "Fleet management · PWA",
    hero_title_1: "Your fleet,",
    hero_title_2: "under control.",
    claim: "Easy, fast and always with you!",
    hero_sub: "Thoth digitises vehicles, fault reports, deadlines, costs and invoices. Operators report a fault by scanning a QR code — you see everything in real time.",
    cta_start: "Start 3-day free trial",
    cta_demo: "Enter the dashboard",
    stat_vehicles: "vehicles managed",
    stat_qr: "no app to download",
    stat_realtime: "real-time data",
    features_kicker: "Everything in one app",
    features_title: "From the fault report to the invoice, paperless",
    f_qr_t: "QR fault reports",
    f_qr_d: "Every vehicle has a QR code: the operator scans it, describes the fault and attaches a photo. No login, no app.",
    f_deadlines_t: "Deadlines & reminders",
    f_deadlines_d: "Inspection, insurance, service and mileage: automatic push and email alerts before they expire.",
    f_costs_t: "Costs & invoices",
    f_costs_d: "Track every expense per vehicle and workshop, upload invoices and keep the fleet budget in check.",
    f_import_t: "Import your data",
    f_import_d: "Bring your archives from Excel/CSV in a few clicks: vehicles, jobs, costs and operators.",
    f_backup_t: "Automatic backups",
    f_backup_d: "Daily safety copy of the whole database, downloadable and restorable whenever you need.",
    f_roles_t: "Roles & permissions",
    f_roles_d: "Admins, warehouse staff and operators: everyone sees what they need. Full activity log.",
    control_kicker: "Control panel",
    control_title: "Your whole fleet at a glance",
    control_d: "Dashboard with vehicle status, open reports, upcoming deadlines and costs. Export everything to CSV or PDF in a snap.",
    control_b1: "Real-time vehicle status",
    control_b2: "Job history per vehicle and workshop",
    control_b3: "Monthly reports and professional exports",
    pricing_kicker: "Simple pricing",
    pricing_title: "One plan, everything included",
    pricing_sub: "Thoth Pro: all features, unlimited vehicles. Start with a 3-day free trial, then cancel anytime.",
    monthly: "Monthly",
    yearly: "Yearly",
    save_badge: "Save 2 months",
    per_month: "/mo",
    per_year: "/yr",
    billed_yearly: "billed annually",
    most_popular: "Most popular",
    choose: "Choose",
    processing: "Please wait…",
    faq_title: "Frequently asked questions",
    faq: [
      { q: "Is there a free trial?", a: "Yes: 3 days with all Pro features, no card required. When it ends, activate a subscription from the Subscription area to keep going — your data stays safe." },
      { q: "Do I need to install anything?", a: "No. Thoth is a web app installable as a PWA on your phone. Operators just use the QR code, with nothing to download." },
      { q: "Can I bring my own data?", a: "Yes, you can import vehicles, jobs, costs and operators from Excel or CSV files yourself." },
      { q: "Are payments secure?", a: "Yes, checkout is handled by Stripe. We never store your card details." },
      { q: "Can I change plan?", a: "Thoth has a single Pro plan with all features included. You can cancel anytime from the Subscription area." },
    ],
    footer_tag: "Easy, fast and always with you!",
    footer_rights: "All rights reserved.",
    op_portal: "Operator portal",
    secure: "Secure payments with Stripe",
    hero_free: "3-day free trial · no card required",
    hero_video_cta: "Watch the demo (45 s)",
    hero_video_close: "Close",
    free_price: "Free",
    free_cta: "Start free",
    subscribe_cta: "Subscribe",
    signup_title: "Create your account",
    signup_sub: "You get a 3-day free trial with all Pro features, no credit card. When it ends, choose whether to subscribe.",
    upgrade_note: "After registration you can complete the secure Stripe payment right away, or close it and start with the 3-day free trial.",
    f_company: "Company name",
    f_firstname: "First name",
    f_lastname: "Last name",
    f_email: "Email",
    f_password: "Password (min 8 chars)",
    signup_btn: "Create account",
    signup_processing: "Creating account…",
    terms_pre: "I accept the ",
    terms_link: "Terms & Conditions",
    terms_post: " and the privacy policy.",
    terms_required: "You must accept the Terms & Conditions",
    signup_success: "Welcome to Thoth! Your 3-day free trial has started.",
    have_account: "Already have an account?",
    login_link: "Log in",
    selected_plan: "Selected plan",
    lifetime_title: "Lifetime purchase",
    lifetime_sub: "Pay once and use Thoth forever: all Pro features, no renewal, no monthly fee.",
    lifetime_cta: "Buy lifetime",
    one_time: "one-time",
    lifetime_payoff: "Pays for itself in ~{y} years vs the annual Pro plan",
    save_yearly: "Save",
    vs_monthly: "/yr vs monthly",
    equiv: "that's",
    sec_kicker: "Security & Trust",
    sec_title: "Your data protected, always",
    sec_d: "Thoth is built with security first: company-isolated data, daily backups and payments handled by Stripe.",
    sec_1_t: "Encrypted, isolated data",
    sec_1_d: "Each company's data is fully separated. Encrypted connections (HTTPS) and passwords protected with bcrypt hashing.",
    sec_2_t: "Automatic daily backups",
    sec_2_d: "A safety copy of the whole database every night, restorable anytime. You never lose anything.",
    sec_3_t: "GDPR compliant",
    sec_3_d: "We follow EU privacy regulations. You can export or request deletion of your data whenever you want.",
    sec_4_t: "Secure payments with Stripe",
    sec_4_d: "Checkout is handled by Stripe, a global payments leader. We never store your card details.",
    sec_payments_soon: "More payment methods coming soon (PayPal, bank transfer and others).",
  },
};

const FEATURE_ICONS = [QrCode, BellRing, Wallet, FileSpreadsheet, DatabaseBackup, ShieldCheck];

export default function Vetrina() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem("thoth_lang") || "it");
  const [billing] = useState("yearly");
  const [plans, setPlans] = useState([]);
  const [checkout, setCheckout] = useState(null);
  const [lifetime, setLifetime] = useState(null);
  const [lifetimeMode, setLifetimeMode] = useState(false);
  const t = T[lang];
  const fmtPrice = (n) => {
    if (n == null) return "";
    const s = Number.isInteger(n) ? String(n) : Number(n).toFixed(2);
    return lang === "it" ? s.replace(".", ",") : s;
  };
  const proYearly = plans.find((p) => p.id === "pro")?.yearly?.amount;
  const payoff = lifetime && proYearly ? Math.max(1, Math.round(lifetime.amount / proYearly)) : null;
  const { signup } = useAuth();
  const [showSignup, setShowSignup] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [signingUp, setSigningUp] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [form, setForm] = useState({ company_name: "", first_name: "", last_name: "", email: "", password: "" });

  useEffect(() => { localStorage.setItem("thoth_lang", lang); }, [lang]);
  useEffect(() => {
    api.get("/payments/plans").then(({ data }) => { setPlans(data.plans || []); setLifetime(data.lifetime || null); }).catch(() => {});
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const openSignup = (plan) => { setSelectedPlan(plan || null); setLifetimeMode(false); setShowSignup(true); };
  const openLifetime = () => { setLifetimeMode(true); setSelectedPlan(null); setShowSignup(true); };
  const doSignup = async (e) => {
    e?.preventDefault?.();
    if (!acceptTerms) { toast.error(t.terms_required); return; }
    setSigningUp(true);
    const res = await signup({ ...form, accept_terms: true });
    if (!res.ok) { setSigningUp(false); toast.error(res.error); return; }
    setSigningUp(false);
    setShowSignup(false);
    if (lifetimeMode && lifetime) {
      setLifetimeMode(false);
      setCheckout({ lookup_key: lifetime.lookup_key, fromSignup: true });
      return;
    }
    const plan = selectedPlan;
    // Tutti i piani sono a pagamento → dopo la registrazione apri il checkout Stripe
    // (utente già loggato, azienda collegata). L'account resta bloccato finché non paga.
    const cycle = billing === "yearly" ? "yearly" : "monthly";
    const lookup_key = plan?.[cycle]?.lookup_key;
    if (lookup_key) {
      setCheckout({ lookup_key, fromSignup: true });
    } else {
      toast.success(t.signup_success);
      navigate("/admin");
    }
  };

  const featureList = [
    { t: t.f_qr_t, d: t.f_qr_d }, { t: t.f_deadlines_t, d: t.f_deadlines_d },
    { t: t.f_costs_t, d: t.f_costs_d }, { t: t.f_import_t, d: t.f_import_d },
    { t: t.f_backup_t, d: t.f_backup_d }, { t: t.f_roles_t, d: t.f_roles_d },
  ];

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 overflow-x-hidden" data-testid="vetrina-page">
      {/* background glows + grid */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[42rem] h-[42rem] bg-blue-600/15 rounded-full blur-[120px]" />
        <div className="absolute top-1/3 -right-40 w-[38rem] h-[38rem] bg-indigo-600/15 rounded-full blur-[120px]" />
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
      </div>

      {/* NAV */}
      <nav className="sticky top-0 z-40 backdrop-blur-xl bg-[#0B0F17]/70 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <button onClick={() => scrollTo("top")} className="flex items-center gap-2.5" data-testid="vetrina-logo">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-700 grid place-items-center shadow-lg shadow-blue-600/30">
              <Zap className="w-5 h-5 text-white" fill="currentColor" />
            </div>
            <span className="font-head font-extrabold text-lg tracking-tight">Thoth</span>
          </button>
          <div className="flex items-center gap-1 sm:gap-3">
            <button onClick={() => scrollTo("features")} className="hidden sm:block text-sm text-slate-400 hover:text-white px-3 py-2 transition-colors">{t.nav_features}</button>
            <button onClick={() => scrollTo("pricing")} className="hidden sm:block text-sm text-slate-400 hover:text-white px-3 py-2 transition-colors">{t.nav_pricing}</button>
            <div className="flex items-center rounded-full border border-white/10 overflow-hidden mr-1" data-testid="lang-switch">
              <button onClick={() => setLang("it")} data-testid="lang-toggle-it" className={`px-2.5 py-1.5 text-xs font-semibold transition-colors ${lang === "it" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}>IT</button>
              <button onClick={() => setLang("en")} data-testid="lang-toggle-en" className={`px-2.5 py-1.5 text-xs font-semibold transition-colors ${lang === "en" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}>EN</button>
            </div>
            <button onClick={() => navigate("/admin/login")} data-testid="vetrina-login-btn" className="text-sm font-semibold px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors">{t.login}</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header id="top" className="relative max-w-6xl mx-auto px-5 pt-14 pb-20 md:pt-20 md:pb-28 grid md:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-blue-300 bg-blue-500/10 border border-blue-500/20 rounded-full px-3 py-1.5">
            <Gauge className="w-3.5 h-3.5" /> {t.hero_badge}
          </span>
          <h1 className="font-head font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]">
            {t.hero_title_1}<br />
            <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-sky-300 bg-clip-text text-transparent">{t.hero_title_2}</span>
          </h1>
          <p className="mt-3 text-lg text-amber-300 font-semibold flex items-center gap-2"><Zap className="w-4 h-4" fill="currentColor" /> {t.claim}</p>
          <p className="mt-4 text-base text-slate-400 max-w-lg leading-relaxed">{t.hero_sub}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => openSignup(null)} data-testid="vetrina-cta-primary" className="group inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-6 py-3.5 rounded-xl font-semibold shadow-lg shadow-blue-600/30 transition-all hover:-translate-y-0.5">
              {t.cta_start} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => navigate("/admin/login")} data-testid="vetrina-cta-demo" className="inline-flex items-center gap-2 border border-white/15 hover:bg-white/5 px-6 py-3.5 rounded-xl font-semibold transition-colors">
              {t.cta_demo}
            </button>
          </div>
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-emerald-300 font-semibold" data-testid="hero-free-badge"><ShieldCheck className="w-4 h-4" /> {t.hero_free}</p>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            {[{ v: "29+", l: t.stat_vehicles, i: Truck }, { v: "0", l: t.stat_qr, i: QrCode }, { v: "24/7", l: t.stat_realtime, i: BellRing }].map((s, i) => (
              <div key={i}>
                <div className="font-head font-extrabold text-2xl bg-gradient-to-r from-blue-300 to-indigo-300 bg-clip-text text-transparent">{s.v}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative fade-up" style={{ animationDelay: "120ms" }}>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 ring-1 ring-blue-500/10">
            <DemoVideo ctaLabel={t.hero_video_cta} closeLabel={t.hero_video_close} />
          </div>
          <div className="absolute -top-4 -right-3 bg-amber-500/90 text-slate-900 rounded-xl px-3 py-2 shadow-xl flex items-center gap-1.5 text-xs font-bold rotate-3">
            <Zap className="w-4 h-4" fill="currentColor" /> Segnalazione QR
          </div>
        </div>
      </header>

      {/* FEATURES */}
      <section id="features" className="relative max-w-6xl mx-auto px-5 py-20">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-400">{t.features_kicker}</p>
          <h2 className="font-head font-extrabold text-3xl sm:text-4xl mt-2 tracking-tight">{t.features_title}</h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featureList.map((f, i) => {
            const Icon = FEATURE_ICONS[i];
            return (
              <div key={i} data-testid={`feature-card-${i}`} className="group bg-[#141B27] border border-white/5 rounded-2xl p-6 hover:border-blue-500/30 hover:-translate-y-1 transition-all duration-300">
                <div className="w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/20 grid place-items-center text-blue-400 group-hover:scale-110 transition-transform">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-head font-bold text-lg mt-4">{f.t}</h3>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTROL */}
      <section className="relative max-w-6xl mx-auto px-5 py-16 grid md:grid-cols-2 gap-12 items-center">
        <div className="relative order-2 md:order-1">
          <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl -rotate-1 hover:rotate-0 transition-transform duration-500">
            <img src={CONTROL_IMG} alt="Flotta dall'alto" className="w-full h-[320px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0F17]/70 to-transparent" />
          </div>
        </div>
        <div className="order-1 md:order-2">
          <p className="text-sm font-semibold uppercase tracking-wider text-indigo-400">{t.control_kicker}</p>
          <h2 className="font-head font-extrabold text-3xl sm:text-4xl mt-2 tracking-tight">{t.control_title}</h2>
          <p className="mt-4 text-slate-400 leading-relaxed">{t.control_d}</p>
          <ul className="mt-6 space-y-3">
            {[t.control_b1, t.control_b2, t.control_b3].map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-200">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/15 text-emerald-400 grid place-items-center shrink-0"><Check className="w-3.5 h-3.5" /></span>
                {b}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SECURITY & TRUST */}
      <section id="security" className="relative max-w-6xl mx-auto px-5 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-wider text-emerald-400">{t.sec_kicker}</p>
          <h2 className="font-head font-extrabold text-3xl sm:text-4xl mt-2 tracking-tight">{t.sec_title}</h2>
          <p className="mt-3 text-slate-400">{t.sec_d}</p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Lock, t: t.sec_1_t, d: t.sec_1_d },
            { icon: DatabaseBackup, t: t.sec_2_t, d: t.sec_2_d },
            { icon: ShieldCheck, t: t.sec_3_t, d: t.sec_3_d },
            { icon: CreditCard, t: t.sec_4_t, d: t.sec_4_d },
          ].map((s, i) => (
            <div key={i} data-testid={`security-card-${i}`} className="bg-[#141B27] border border-white/5 rounded-2xl p-5 hover:border-emerald-500/30 transition-colors">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/10 text-emerald-400 grid place-items-center"><s.icon className="w-5 h-5" /></div>
              <h3 className="font-head font-bold mt-4">{s.t}</h3>
              <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2" data-testid="security-payments-soon"><CreditCard className="w-4 h-4 text-slate-400" /> {t.sec_payments_soon}</p>
      </section>

      {/* PRICING */}
      <section id="pricing" className="relative max-w-6xl mx-auto px-5 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-semibold uppercase tracking-wider text-blue-400">{t.pricing_kicker}</p>
          <h2 className="font-head font-extrabold text-3xl sm:text-4xl mt-2 tracking-tight">{t.pricing_title}</h2>
          <p className="mt-3 text-slate-400">{t.pricing_sub}</p>
        </div>

        {/* billing toggle rimosso: unico piano annuale */}

        <div className="mt-10 grid gap-6 max-w-md mx-auto">
          {plans.map((p) => {
            const isPersonal = p.id === "personal";
            const price = billing === "yearly" ? p.yearly?.amount : p.monthly?.amount;
            const feats = (p.features && p.features[lang]) || [];
            const highlight = p.highlight;
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`}
                className={`relative rounded-3xl p-8 border transition-all duration-300 hover:-translate-y-1 ${highlight ? "bg-gradient-to-b from-blue-600/15 to-[#141B27] border-blue-500/40 shadow-2xl shadow-blue-600/20" : "bg-[#141B27] border-white/8"}`}>
                {highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-lg">{t.most_popular}</span>
                )}
                <div className="flex items-center gap-2">
                  {highlight ? <Receipt className="w-5 h-5 text-blue-400" /> : isPersonal ? <Gift className="w-5 h-5 text-emerald-400" /> : <Truck className="w-5 h-5 text-slate-400" />}
                  <h3 className="font-head font-extrabold text-xl">{p.name}</h3>
                </div>
                <div className="mt-5 flex items-end gap-1 min-h-[3.5rem]">
                  <span className="font-head font-extrabold text-5xl tracking-tight">{fmtPrice(price)}€</span>
                  <span className="text-slate-400 mb-1.5 text-sm">{billing === "yearly" ? t.per_year : t.per_month}</span>
                </div>
                {billing === "yearly" && <p className="text-xs text-slate-500 mt-1">{t.billed_yearly}</p>}
                <button onClick={() => openSignup(p)} data-testid={`plan-cta-${p.id}`}
                  className={`mt-5 w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold transition-all ${highlight ? "bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-600/30 hover:-translate-y-0.5" : isPersonal ? "bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-600/30 hover:-translate-y-0.5" : "bg-white/10 hover:bg-white/20"}`}>
                  {t.subscribe_cta} <ArrowRight className="w-4 h-4" />
                </button>
                <ul className="mt-7 space-y-3">
                  {feats.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/15 text-emerald-400 grid place-items-center shrink-0"><Check className="w-3.5 h-3.5" /></span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {lifetime && (
          <div className="mt-6 max-w-4xl mx-auto rounded-3xl p-6 sm:p-7 border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-[#141B27] flex flex-col sm:flex-row items-center justify-between gap-5" data-testid="lifetime-card">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 grid place-items-center shrink-0"><Infinity className="w-6 h-6 text-amber-400" /></div>
              <div>
                <h3 className="font-head font-extrabold text-xl">{t.lifetime_title}</h3>
                <p className="text-sm text-slate-400 mt-1 max-w-md">{t.lifetime_sub}</p>
                {payoff ? <p className="text-xs text-amber-300 mt-2 font-semibold" data-testid="lifetime-payoff">{t.lifetime_payoff.replace("{y}", payoff)}</p> : null}
              </div>
            </div>
            <div className="flex items-center gap-5 shrink-0">
              <div className="text-right"><div className="font-head font-extrabold text-3xl">{lifetime.amount}€</div><div className="text-xs text-slate-500">{t.one_time}</div></div>
              <button onClick={openLifetime} data-testid="plan-lifetime" className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-6 py-3.5 rounded-xl flex items-center gap-2 transition-all hover:-translate-y-0.5">{t.lifetime_cta} <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-slate-500 mt-8 flex items-center justify-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-500" /> {t.secure}</p>
      </section>

      {/* FAQ */}
      <section className="relative max-w-3xl mx-auto px-5 py-16">
        <h2 className="font-head font-extrabold text-2xl sm:text-3xl text-center tracking-tight">{t.faq_title}</h2>
        <div className="mt-8 space-y-3">
          {t.faq.map((f, i) => (
            <details key={i} data-testid={`faq-${i}`} className="group bg-[#141B27] border border-white/5 rounded-2xl p-5 open:border-blue-500/30 transition-colors">
              <summary className="cursor-pointer list-none flex items-center justify-between font-semibold text-slate-100">
                {f.q}
                <span className="text-blue-400 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative border-t border-white/5 mt-10">
        <div className="max-w-6xl mx-auto px-5 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-700 grid place-items-center"><Zap className="w-4 h-4 text-white" fill="currentColor" /></div>
            <div>
              <div className="font-head font-bold leading-none">Thoth</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{t.footer_tag}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Globe className="w-4 h-4" /> © {new Date().getFullYear()} Thoth · {t.footer_rights}
          </div>
          <div className="flex items-center gap-4 text-xs">
            <a href="/termini" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-termini-link">{t.terms_link}</a>
            <a href="/privacy" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</a>
          </div>
        </div>
      </footer>

      {showSignup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="signup-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !signingUp && setShowSignup(false)} />
          <div className="relative w-full max-w-md bg-[#141B27] border border-white/10 rounded-3xl p-6 shadow-2xl fade-up max-h-[92vh] overflow-y-auto">
            <button onClick={() => !signingUp && setShowSignup(false)} data-testid="signup-close" className="absolute top-4 right-4 text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
            <h3 className="font-head font-extrabold text-xl">{t.signup_title}</h3>
            <p className="text-sm text-slate-400 mt-1">{t.signup_sub}</p>
            {selectedPlan && (
              <div className="mt-3 inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-300 rounded-full px-3 py-1 text-xs font-semibold" data-testid="signup-selected-plan">
                {t.selected_plan}: {selectedPlan.name}{` · ${billing === "yearly" ? t.yearly : t.monthly}`}
              </div>
            )}
            {selectedPlan && (
              <p className="text-xs text-blue-300/80 mt-2" data-testid="signup-upgrade-note">{t.upgrade_note}</p>
            )}
            <form onSubmit={doSignup} className="mt-5 space-y-3">
              <input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder={t.f_company} data-testid="signup-company" className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-blue-500" />
              <div className="grid grid-cols-2 gap-3">
                <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder={t.f_firstname} data-testid="signup-firstname" className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-blue-500" />
                <input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder={t.f_lastname} data-testid="signup-lastname" className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-blue-500" />
              </div>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.f_email} data-testid="signup-email" className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-blue-500" />
              <input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={t.f_password} data-testid="signup-password" className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-blue-500" />
              <label className="flex items-start gap-2.5 text-xs text-slate-400 cursor-pointer">
                <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} data-testid="signup-terms" className="mt-0.5 w-4 h-4 accent-blue-600 shrink-0" />
                <span>{t.terms_pre}<Link to="/termini" target="_blank" className="text-blue-400 underline hover:text-blue-300">{t.terms_link}</Link>{t.terms_post}</span>
              </label>
              <button type="submit" disabled={signingUp} data-testid="signup-submit" className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 py-3 rounded-xl font-semibold transition-colors">
                {signingUp ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.signup_processing}</> : <>{t.signup_btn} <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
            <p className="text-center text-xs text-slate-500 mt-4">{t.have_account} <button onClick={() => navigate("/admin/login")} className="text-blue-400 hover:text-blue-300 font-semibold">{t.login_link}</button></p>
          </div>
        </div>
      )}

      {checkout && (
        <EmbeddedCheckoutModal
          lookupKey={checkout.lookup_key}
          lang={lang}
          onClose={() => { const fromSignup = checkout.fromSignup; setCheckout(null); if (fromSignup) navigate("/admin"); }}
        />
      )}
    </div>
  );
}
